/**
 * Parse ServiceTitan "Review & Finish" paste text into Rate Card draft fields.
 * Pure function — no network, no photo/upload dependencies.
 */

import type { MarkupTierDraft } from "./rateCardPreview";

export type ParsedFeeRule = {
    kind: "category_markup" | "item_flat" | "category_flat" | "hourly_override";
    label: string;
    categoryPath?: string;
    itemName?: string;
    amount?: number;
    markupPct?: number;
};

export type ParsedRateCardPaste = {
    billingMode: "time_and_materials";
    testFeeFlat: number | null;
    testFeeFirelineMain: number | null;
    hourlyLaborRate: number | null;
    afterHoursLaborRate: number | null;
    materialTiers: MarkupTierDraft[];
    fees: ParsedFeeRule[];
    proposedRules: ParsedFeeRule[];
    warnings: string[];
    summaryLines: string[];
};

function parseMoney(s: string): number | null {
    const m = s.replace(/[$,]/g, "").trim();
    const n = Number(m);
    return Number.isFinite(n) ? n : null;
}

function parseMultiplier(s: string): number | null {
    const m = s.replace(/[x×]/gi, "").trim();
    const n = Number(m);
    return Number.isFinite(n) ? n : null;
}

function isMoneyLine(line: string): boolean {
    return /^\$\s*[\d,]+(?:\.\d{2})?$/.test(line.trim());
}

function isBareAmountLine(line: string): boolean {
    return /^[\d,]+(?:\.\d{2})?$/.test(line.trim());
}

/** ST table paste often splits `$` and `95.00` onto separate lines. */
function normalizePasteLines(raw: string): string[] {
    const rawLines = raw.split(/\n/).map((l) => l.trim());
    const out: string[] = [];
    for (let i = 0; i < rawLines.length; i++) {
        const line = rawLines[i]!;
        if (line === "$" && i + 1 < rawLines.length) {
            const next = rawLines[i + 1]!.trim();
            if (isBareAmountLine(next)) {
                out.push(`$${next}`);
                i++;
                continue;
            }
        }
        if (line) out.push(line);
    }
    return out;
}

function isPathLine(line: string): boolean {
    return line.includes(">") || /^Test Reporting/i.test(line) || /^After Hours Labor/i.test(line);
}

function isSectionHeader(line: string): boolean {
    return /^(Material|Service|Labor|Exceptions|Category|Item)$/i.test(line);
}

function isSkippableTableNoise(line: string): boolean {
    return (
        /^(Name|Item Type|Rule|\(optional\))$/i.test(line) ||
        /^Set markup exceptions/i.test(line) ||
        /^Material\s*-\s*Category$/i.test(line) ||
        /^Service\s*-\s*Category$/i.test(line) ||
        /^Service\s*-\s*Category$/i.test(line)
    );
}

/** Scan lines near a category path for dollar amounts (ST often puts $ before the path). */
function findAmountNearPath(lines: string[], pathIndex: number): { amount: number | null; markupPct: number | null } {
    const end = Math.min(lines.length - 1, pathIndex + 8);

    // ST Labor section: amount on the line immediately before the path (reverse paste order)
    if (pathIndex > 0 && isMoneyLine(lines[pathIndex - 1]!)) {
        const prevPrev = pathIndex >= 2 ? lines[pathIndex - 2]! : "";
        const moneyBelongsToPriorForwardBlock =
            isPathLine(prevPrev) ||
            /^Category$/i.test(prevPrev) ||
            /^\$?\s*Amount/i.test(prevPrev) ||
            prevPrev === "$ Amount";
        if (!moneyBelongsToPriorForwardBlock) {
            const v = parseMoney(lines[pathIndex - 1]!);
            if (v != null) return { amount: v, markupPct: null };
        }
    }

    let amountAfterHeader: number | null = null;
    let markupAfterHeader: number | null = null;

    for (let j = pathIndex + 1; j <= end; j++) {
        const line = lines[j]!;
        if (isPathLine(line) && !isMoneyLine(line)) break;

        if (/^Service\s*-\s*Category$/i.test(line) || /^Category$/i.test(line)) continue;
        if (isSkippableTableNoise(line)) continue;

        if (/^\$?\s*Amount/i.test(line) || line === "$ Amount") {
            for (let k = j + 1; k <= Math.min(j + 4, end); k++) {
                const moneyLine = lines[k]!;
                if (isPathLine(moneyLine) && !isMoneyLine(moneyLine)) break;
                if (isSkippableTableNoise(moneyLine)) continue;
                if (isMoneyLine(moneyLine)) {
                    amountAfterHeader = parseMoney(moneyLine);
                    break;
                }
                if (isBareAmountLine(moneyLine)) {
                    amountAfterHeader = parseMoney(moneyLine);
                    break;
                }
            }
            if (amountAfterHeader != null) break;
            continue;
        }

        if (/^\$?\s*Markup/i.test(line) || line === "$ Markup") {
            for (let k = j + 1; k <= Math.min(j + 4, end); k++) {
                const moneyLine = lines[k]!;
                if (isPathLine(moneyLine) && !isMoneyLine(moneyLine)) break;
                if (isSkippableTableNoise(moneyLine)) continue;
                if (isMoneyLine(moneyLine)) {
                    markupAfterHeader = parseMoney(moneyLine);
                    break;
                }
                if (isBareAmountLine(moneyLine)) {
                    markupAfterHeader = parseMoney(moneyLine);
                    break;
                }
            }
        }
    }

    return { amount: amountAfterHeader, markupPct: markupAfterHeader };
}

function parseMaterialTiers(text: string): MarkupTierDraft[] {
    const tiers: MarkupTierDraft[] = [];
    const rangeRe = /\$?([\d,]+(?:\.\d{2})?)\s+to\s+\$?([\d,]+(?:\.\d{2})?)/gi;
    const multRe = /([\d.]+)\s*[x×]/gi;
    const materialsIdx = text.search(/Materials/i);
    const slice = materialsIdx >= 0 ? text.slice(materialsIdx) : text;
    const exceptionsIdx = slice.search(/Exceptions/i);
    const block = exceptionsIdx >= 0 ? slice.slice(0, exceptionsIdx) : slice.slice(0, 2000);

    const ranges: Array<{ min: string; max: string | null }> = [];
    let rm: RegExpExecArray | null;
    const rangeCopy = block;
    while ((rm = rangeRe.exec(rangeCopy)) !== null) {
        const min = rm[1]!.replace(/,/g, "");
        let max = rm[2]!.replace(/,/g, "");
        if (Number(max) >= 999999999) max = "";
        ranges.push({ min, max: max ? max : null });
    }

    const mults: number[] = [];
    let mm: RegExpExecArray | null;
    while ((mm = multRe.exec(block)) !== null) {
        const v = parseMultiplier(mm[0]);
        if (v != null && v > 0 && v < 10) mults.push(v);
    }

    for (let i = 0; i < ranges.length; i++) {
        tiers.push({
            min_amount: ranges[i]!.min,
            max_amount: ranges[i]!.max,
            multiplier: String(mults[i] ?? mults[mults.length - 1] ?? 1),
            sort_order: i,
        });
    }
    return tiers;
}

function parseLaborSectionRate(lines: string[]): number | null {
    const idx = lines.findIndex((l) => /^Labor$/i.test(l));
    if (idx < 0) return null;
    for (let i = idx + 1; i < Math.min(idx + 8, lines.length); i++) {
        if (lines[i] === "Service") {
            const { amount } = findAmountNearPath(lines, i);
            if (amount != null && amount > 0) return amount;
        }
        if (isPathLine(lines[i]!) && lines[i]!.includes(">")) break;
    }
    return null;
}

function parseExceptions(text: string): ParsedFeeRule[] {
    const rules: ParsedFeeRule[] = [];
    const exceptionsIdx = text.search(/Exceptions/i);
    if (exceptionsIdx < 0) return rules;
    const lines = normalizePasteLines(text.slice(exceptionsIdx)).filter(
        (l) => !/^(Name|Item Type|Rule|\(optional\))$/i.test(l) && !/^Set markup exceptions/i.test(l),
    );

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i]!;
        if (!isPathLine(line)) continue;
        if (isSectionHeader(line)) continue;

        const path = line;
        const { amount, markupPct } = findAmountNearPath(lines, i);

        const isTesting =
            /backflow testing/i.test(path) && !/domestic|fireline|\(\d/i.test(path) && !/city fee|permit/i.test(path);
        const isFirelineVariant = /backflow testing \(fireline\)|backflow testing \(domestic\)/i.test(path);
        const isRepairLabor =
            /\(\d+\s*tech/i.test(path) || (/repair|replacement|valve repair/i.test(path) && amount != null);
        const isCityFee = /city fee/i.test(path);
        const isPermitFee = /permit fee/i.test(path);
        const isItemFee =
            /^Test Reporting/i.test(path) ||
            (/compliance fee|admin fee/i.test(path) && !path.includes(">") && amount != null);
        const isAfterHours = /after hours/i.test(path);

        if (isCityFee || isPermitFee) {
            rules.push({
                kind: "category_markup",
                label: path,
                categoryPath: path,
                markupPct: markupPct ?? amount ?? 0,
            });
            continue;
        }

        if (isItemFee && amount != null) {
            rules.push({
                kind: "item_flat",
                label: path,
                itemName: path,
                amount,
            });
            continue;
        }

        if ((isTesting || isFirelineVariant) && amount != null) {
            rules.push({
                kind: "category_flat",
                label: path,
                categoryPath: path,
                amount,
            });
            continue;
        }

        if (isRepairLabor && amount != null) {
            const techMatch = path.match(/\((\d+)\s*tech/i);
            const crew = techMatch ? Number(techMatch[1]) : 1;
            rules.push({
                kind: "hourly_override",
                label: path,
                categoryPath: path,
                amount: crew > 0 ? amount / crew : amount,
            });
            continue;
        }

        if (isAfterHours && amount != null) {
            rules.push({
                kind: "hourly_override",
                label: "After hours labor",
                amount,
            });
        }
    }

    const laborSectionRate = parseLaborSectionRate(lines);
    if (laborSectionRate != null) {
        rules.push({
            kind: "hourly_override",
            label: "Labor service rate",
            amount: laborSectionRate,
        });
    }

    return rules;
}

function pickBestByPathLength(candidates: Array<{ path: string; amount: number }>): number | null {
    if (!candidates.length) return null;
    candidates.sort((a, b) => b.path.length - a.path.length);
    return candidates[0]!.amount;
}

function deriveTopLevelFields(rules: ParsedFeeRule[], tiers: MarkupTierDraft[]) {
    const flatCandidates: Array<{ path: string; amount: number }> = [];
    const firelineCandidates: Array<{ path: string; amount: number }> = [];
    let hourlyLaborRate: number | null = null;
    let afterHoursLaborRate: number | null = null;

    for (const r of rules) {
        if (r.kind === "category_flat" && r.categoryPath && r.amount != null && r.amount > 0) {
            const path = r.categoryPath;
            if (/backflow testing \(fireline\)/i.test(path) && !/domestic/i.test(path)) {
                firelineCandidates.push({ path, amount: r.amount });
            } else if (/backflow testing \(domestic\)/i.test(path)) {
                flatCandidates.push({ path, amount: r.amount });
            } else if (/backflow testing/i.test(path) && !/city fee|permit|\(fireline\)|\(domestic\)/i.test(path)) {
                flatCandidates.push({ path, amount: r.amount });
            } else if (/fireline/i.test(path) && !/domestic/i.test(path) && /backflow testing/i.test(path)) {
                firelineCandidates.push({ path, amount: r.amount });
            }
        }
        if (r.kind === "hourly_override" && r.amount != null && r.amount > 0 && /after hours/i.test(r.label)) {
            afterHoursLaborRate = r.amount;
        }
    }

    const oneTech = rules.find(
        (r) => r.kind === "hourly_override" && /1 tech/i.test(r.label) && r.amount != null && r.amount > 0,
    );
    const laborService = rules.find(
        (r) => r.kind === "hourly_override" && /labor service rate/i.test(r.label) && r.amount != null && r.amount > 0,
    );
    if (oneTech?.amount != null) {
        hourlyLaborRate = oneTech.amount;
    } else if (laborService?.amount != null) {
        hourlyLaborRate = laborService.amount;
    } else {
        const repairRates = rules
            .filter(
                (r) =>
                    r.kind === "hourly_override" && !/after hours/i.test(r.label) && r.amount != null && r.amount > 0,
            )
            .map((r) => r.amount!);
        if (repairRates.length) hourlyLaborRate = Math.min(...repairRates);
    }

    const testFeeFlat = pickBestByPathLength(flatCandidates);
    const testFeeFirelineMain = pickBestByPathLength(firelineCandidates);

    return { testFeeFlat, testFeeFirelineMain, hourlyLaborRate, afterHoursLaborRate, materialTiers: tiers };
}

function positiveOrNull(v: number | null | undefined): number | null {
    return v != null && v > 0 ? v : null;
}

export function parseRateCardPaste(raw: string): ParsedRateCardPaste {
    const warnings: string[] = [];
    const text = raw.trim();
    if (!text) {
        return {
            billingMode: "time_and_materials",
            testFeeFlat: null,
            testFeeFirelineMain: null,
            hourlyLaborRate: null,
            afterHoursLaborRate: null,
            materialTiers: [],
            fees: [],
            proposedRules: [],
            warnings: ["Paste is empty."],
            summaryLines: [],
        };
    }

    const materialTiers = parseMaterialTiers(text);
    const proposedRules = parseExceptions(text);
    const derived = deriveTopLevelFields(proposedRules, materialTiers);

    const testFeeFlat = positiveOrNull(derived.testFeeFlat);
    const testFeeFirelineMain = positiveOrNull(derived.testFeeFirelineMain);
    const hourlyLaborRate = positiveOrNull(derived.hourlyLaborRate);
    const afterHoursLaborRate = positiveOrNull(derived.afterHoursLaborRate);

    const fees = proposedRules.filter(
        (r) => r.kind === "category_markup" || (r.kind === "item_flat" && /fee|compliance|admin/i.test(r.label)),
    );

    const summaryLines: string[] = [];
    if (testFeeFlat != null) summaryLines.push(`Standard test fee: $${testFeeFlat.toFixed(2)}`);
    if (testFeeFirelineMain != null) summaryLines.push(`Fireline main test fee: $${testFeeFirelineMain.toFixed(2)}`);
    if (hourlyLaborRate != null) summaryLines.push(`Hourly labor rate: $${hourlyLaborRate.toFixed(2)}/hr`);
    if (materialTiers.length) summaryLines.push(`${materialTiers.length} material markup tier(s)`);
    if (fees.length) summaryLines.push(`${fees.length} fee rule(s)`);
    if (proposedRules.length > fees.length)
        summaryLines.push(`${proposedRules.length - fees.length} other exception(s) — review in Advanced`);

    if (!summaryLines.length) warnings.push("Could not extract structured pricing — check paste format.");

    return {
        billingMode: "time_and_materials",
        testFeeFlat,
        testFeeFirelineMain,
        hourlyLaborRate,
        afterHoursLaborRate,
        materialTiers,
        fees,
        proposedRules,
        warnings,
        summaryLines,
    };
}
